import { useState } from 'react'
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Box,
  Button,
  Badge,
  Modal,
  DataTable,
  Pagination,
  Banner,
  SkeletonBodyText,
  EmptyState,
  Divider,
  TextField,
} from '@shopify/polaris'
import { useQuery } from '@tanstack/react-query'
import { api, Message } from '../api/client'
import { useShopifyAuth } from '../hooks/useShopifyAuth'

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// Get badge tone for status
function getStatusBadge(status: string) {
  switch (status) {
    case 'converted':
      return <Badge tone="success">Purchased</Badge>
    case 'delivered':
      return <Badge tone="warning">Delivered</Badge>
    case 'sent':
      return <Badge tone="info">Sent</Badge>
    case 'failed':
      return <Badge tone="critical">Failed</Badge>
    default:
      return <Badge>{status}</Badge>
  }
}

// Message Detail Modal
function MessageDetailModal({
  message,
  open,
  onClose,
}: {
  message: Message | null
  open: boolean
  onClose: () => void
}) {
  if (!message) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Message Details"
      primaryAction={{
        content: 'Close',
        onAction: onClose,
      }}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Info Grid */}
          <BlockStack gap="200">
            <InlineStack align="space-between">
              <Text as="span" tone="subdued">Sent</Text>
              <Text as="span" fontWeight="semibold">
                {formatDate(message.sent_at)}
              </Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text as="span" tone="subdued">Customer</Text>
              <Text as="span" fontWeight="semibold">
                {message.customer_phone_masked}
              </Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text as="span" tone="subdued">Cart Value</Text>
              <Text as="span" fontWeight="semibold">
                ${message.cart_value.toFixed(2)}
              </Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text as="span" tone="subdued">Offer</Text>
              <Text as="span" fontWeight="semibold">
                {message.offer_description}
              </Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text as="span" tone="subdued">Status</Text>
              {getStatusBadge(message.status)}
            </InlineStack>
            {message.coupon_code && (
              <InlineStack align="space-between">
                <Text as="span" tone="subdued">Coupon Code</Text>
                <Badge tone="info">{message.coupon_code}</Badge>
              </InlineStack>
            )}
          </BlockStack>

          <Divider />

          {/* Message Content */}
          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">
              Message
            </Text>
            <Box
              background="bg-surface-secondary"
              padding="400"
              borderRadius="200"
            >
              <Text as="p" variant="bodyMd">
                {message.message_content || 'Message content not available'}
              </Text>
            </Box>
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  )
}

export default function History() {
  // Initialize Shopify auth
  useShopifyAuth()

  // Check if we're in preview mode
  const isPreviewMode = !window.shopify

  // Mock data for preview
  const mockMessages: { messages: Message[], total: number, page: number, pages: number } = {
    messages: [
      {
        id: '1',
        sent_at: '2024-01-28T14:30:00Z',
        customer_phone: '+14155554567',
        customer_phone_masked: '+1 ***-***-4567',
        offer_type: 'percentage_off',
        offer_description: '15% Off',
        cart_value: 89.99,
        status: 'delivered',
        coupon_code: 'SAVE15',
        message_content: "Hey Sarah! 👋\n\nNoticed you left some amazing items in your cart at TikiShop!\n\nHere's 15% off to complete your order. Use code: SAVE15\n\nTap here to checkout: shop.link/abc123",
      },
      {
        id: '2',
        sent_at: '2024-01-27T10:15:00Z',
        customer_phone: '+14155558901',
        customer_phone_masked: '+1 ***-***-8901',
        offer_type: 'free_shipping',
        offer_description: 'Free Shipping',
        cart_value: 124.50,
        status: 'delivered',
        coupon_code: 'FREESHIP',
        message_content: "Hi John! Your cart is waiting for you at TikiShop. We'll throw in FREE shipping if you complete your order today! Use code: FREESHIP",
      },
      {
        id: '3',
        sent_at: '2024-01-26T16:45:00Z',
        customer_phone: '+14155552345',
        customer_phone_masked: '+1 ***-***-2345',
        offer_type: 'combined',
        offer_description: '10% Off + Free Shipping',
        cart_value: 215.00,
        status: 'sent',
        coupon_code: 'COMBO10',
        message_content: "Hey there! Don't miss out on your TikiShop cart. Get 10% off AND free shipping with code COMBO10!",
      },
      {
        id: '4',
        sent_at: '2024-01-25T09:20:00Z',
        customer_phone: '+14155556789',
        customer_phone_masked: '+1 ***-***-6789',
        offer_type: 'percentage_off',
        offer_description: '20% Off',
        cart_value: 67.25,
        status: 'failed',
        coupon_code: 'SAVE20',
        message_content: "Hi! Your TikiShop cart misses you. Here's 20% off: SAVE20",
      },
    ],
    total: 47,
    page: 1,
    pages: 5,
  }

  // State
  const [page, setPage] = useState(1)
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const limit = 10

  // Fetch messages - disabled in preview mode
  const { data: apiData, isLoading, error } = useQuery({
    queryKey: ['messages', page, limit],
    queryFn: () => api.getMessages(page, limit),
    enabled: !isPreviewMode,
  })

  const data = isPreviewMode ? mockMessages : apiData

  // Handle view message
  const handleViewMessage = async (messageId: string) => {
    // In preview mode, find from mock data
    if (isPreviewMode) {
      const message = mockMessages.messages.find(m => m.id === messageId)
      if (message) {
        setSelectedMessage(message)
        setModalOpen(true)
      }
      return
    }

    try {
      const message = await api.getMessage(messageId)
      setSelectedMessage(message)
      setModalOpen(true)
    } catch (err) {
      console.error('Failed to fetch message:', err)
    }
  }

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedMessage(null)
  }

  // Format table rows
  const rows = data?.messages.map((message) => [
    formatDate(message.sent_at),
    message.customer_phone_masked,
    message.offer_description,
    `$${message.cart_value.toFixed(2)}`,
    getStatusBadge(message.status),
    <Button key={message.id} variant="plain" onClick={() => handleViewMessage(message.id)}>
      View
    </Button>,
  ]) || []

  if (error && !isPreviewMode) {
    return (
      <Page title="Message History">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load message history. Please refresh the page.</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page title="Message History">
      <Layout>
        {/* Search and Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="space-between">
              <Box width="300px">
                <TextField
                  label=""
                  placeholder="Search by phone number..."
                  value={searchQuery}
                  onChange={setSearchQuery}
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setSearchQuery('')}
                />
              </Box>
              <Text as="p" tone="subdued">
                {data ? `${data.total} messages total` : 'Loading...'}
              </Text>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Messages Table */}
        <Layout.Section>
          <Card>
            {isLoading ? (
              <SkeletonBodyText lines={10} />
            ) : rows.length === 0 ? (
              <EmptyState
                heading="No messages yet"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  Once you start recovering abandoned carts, your message history will appear here.
                </p>
              </EmptyState>
            ) : (
              <BlockStack gap="400">
                <DataTable
                  columnContentTypes={[
                    'text',
                    'text',
                    'text',
                    'numeric',
                    'text',
                    'text',
                  ]}
                  headings={[
                    'Date/Time',
                    'Customer',
                    'Offer',
                    'Cart Value',
                    'Status',
                    '',
                  ]}
                  rows={rows}
                />

                {/* Pagination */}
                {data && data.pages > 1 && (
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={page > 1}
                      onPrevious={() => setPage(page - 1)}
                      hasNext={page < data.pages}
                      onNext={() => setPage(page + 1)}
                    />
                  </InlineStack>
                )}
              </BlockStack>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Message Detail Modal */}
      <MessageDetailModal
        message={selectedMessage}
        open={modalOpen}
        onClose={handleCloseModal}
      />
    </Page>
  )
}
